import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TabUsageEntry {
	count: number;
	lastUsed: string;
}

type TabUsageMap = Record<string, TabUsageEntry>;

const getCacheKey = (userId: string, entityType: string) =>
	`tab-usage-${userId}-${entityType}`;

const readCache = (userId: string, entityType: string): TabUsageMap => {
	try {
		const raw = localStorage.getItem(getCacheKey(userId, entityType));
		if (raw) return JSON.parse(raw);
	} catch {
		// ignore
	}
	return {};
};

const writeCache = (userId: string, entityType: string, data: TabUsageMap) => {
	try {
		localStorage.setItem(getCacheKey(userId, entityType), JSON.stringify(data));
	} catch {
		// ignore
	}
};

export function useTabUsage(userId: string | undefined, entityType: string) {
	const [usage, setUsage] = useState<TabUsageMap>(() => {
		if (userId) return readCache(userId, entityType);
		return {};
	});
	const fetchedRef = useRef(false);

	// Fetch from Supabase on mount
	useEffect(() => {
		if (!userId || fetchedRef.current) return;
		fetchedRef.current = true;

		const fetchUsage = async () => {
			// @ts-ignore - table may not be in generated types yet
			const { data, error } = await supabase
				.from("tab_usage")
				.select("tab_id, count, last_used")
				.eq("user_id", userId)
				.eq("entity_type", entityType);

			if (error || !data) return;

			const map: TabUsageMap = {};
			for (const row of data) {
				map[row.tab_id] = {
					count: row.count,
					lastUsed: row.last_used,
				};
			}
			setUsage(map);
			writeCache(userId, entityType, map);
		};

		fetchUsage();
	}, [userId, entityType]);

	const trackUsage = useCallback(
		(tabId: string) => {
			if (!userId) return;

			const now = new Date().toISOString();
			setUsage((prev) => {
				const existing = prev[tabId];
				const updated = {
					...prev,
					[tabId]: {
						count: (existing?.count || 0) + 1,
						lastUsed: now,
					},
				};
				writeCache(userId, entityType, updated);
				return updated;
			});

			// Fire-and-forget Supabase upsert
			// @ts-ignore - table may not be in generated types yet
			supabase
				.from("tab_usage")
				.upsert(
					{
						user_id: userId,
						entity_type: entityType,
						tab_id: tabId,
						last_used: now,
						count: (usage[tabId]?.count || 0) + 1,
					},
					{ onConflict: "user_id,entity_type,tab_id" }
				)
				.then();
		},
		[userId, entityType, usage]
	);

	const getDynamicTabs = useCallback(
		(
			allTabIds: string[],
			pinnedIds: string[],
			slotCount: number,
			defaultIds: string[]
		): string[] => {
			const nonPinned = allTabIds.filter((id) => !pinnedIds.includes(id));
			const hasUsageData = nonPinned.some((id) => usage[id]);

			if (!hasUsageData)
				return defaultIds.slice(0, slotCount);

			return nonPinned
				.filter((id) => usage[id])
				.sort((a, b) => {
					const aEntry = usage[a];
					const bEntry = usage[b];
					const timeDiff = new Date(bEntry.lastUsed).getTime() - new Date(aEntry.lastUsed).getTime();
					if (timeDiff !== 0) return timeDiff;
					return bEntry.count - aEntry.count;
				})
				.slice(0, slotCount)
				.concat(
					// Fill remaining slots with defaults if not enough usage data
					defaultIds.filter(
						(id) =>
							!usage[id] &&
							!pinnedIds.includes(id)
					)
				)
				.slice(0, slotCount);
		},
		[usage]
	);

	return { usage, trackUsage, getDynamicTabs };
}
